import { contrast, hexToRgb, oklchToHex, rgbToOklch } from './color';

// Brand tokens for the customer website and app (docs/architecture/planned/online-ordering.md, "Customer design system"):
// scales in OKLCH from a brand colour, semantic roles for light and dark picked by WCAG contrast, and the non-colour
// foundation. Ported from tools/design-tokens/generate_brand_tokens.py; the same input gives the same tokens.

export type Scale = Record<string, string>;
export type Roles = Record<string, string>;

/** Everything the generator reads. A brand site's theme maps onto it with {@link fromBrandSite}. */
export interface BrandInput {
  name: string;
  primary: string;
  accent?: string;
  /** Body text colour; defaults to neutral-900. */
  text?: string;
  neutralTint?: 'cool' | 'warm' | 'pure';
  /** The colour whose hue tints the greys; defaults to the primary. */
  neutralTintFrom?: string;
  radius?: 'sharp' | 'soft' | 'round';
  /** False: light only. */
  darkMode?: boolean;
  fonts: Record<string, string>;
  /** Roles the brand sets itself (a page grey, a header colour…); they go through the same checks. */
  roles?: Roles;
  darkRoles?: Roles;
}

export interface BrandTokens {
  scales: Record<string, Scale>;
  light: Roles;
  dark: Roles;
}

export const STEPS: Record<string, number> = {
  '50': 0.975, '100': 0.94, '200': 0.885, '300': 0.8, '400': 0.71, '500': 0.635,
  '600': 0.56, '700': 0.48, '800': 0.4, '900': 0.32, '950': 0.24,
};

/** Chroma relative to the source at each step: softer at the ends so tints stay clean. */
const CHROMA: Record<string, number> = {
  '50': 0.12, '100': 0.25, '200': 0.45, '300': 0.7, '400': 0.9, '500': 1.0,
  '600': 1.0, '700': 0.92, '800': 0.8, '900': 0.65, '950': 0.5,
};

const STATUS: Record<string, string> = { success: '#16A34A', warning: '#F59E0B', danger: '#DC2626', info: '#2563EB' };

const WHITE = '#FFFFFF';

const at = (scale: Scale, step: string) => scale[step] as string;

export function scale(baseHex: string): Scale {
  const [, C, H] = rgbToOklch(hexToRgb(baseHex));
  const out: Scale = {};
  for (const [step, lightness] of Object.entries(STEPS)) {
    out[step] = oklchToHex(lightness, C * (CHROMA[step] as number), H);
  }
  out.base = baseHex.toUpperCase();
  return out;
}

/** A near-grey scale with a slight hue from the brand (warm or cool), or pure grey. */
function neutralScale(tintHex: string, tint: 'cool' | 'warm' | 'pure'): Scale {
  let [, , H] = rgbToOklch(hexToRgb(tintHex));
  const c = { pure: 0.0, warm: 0.006, cool: 0.006 }[tint];
  if (tint === 'cool') {
    H = 250;
  }
  const out: Scale = { '0': WHITE };
  for (const [step, lightness] of Object.entries(STEPS)) {
    out[step] = oklchToHex(step !== '950' ? lightness : 0.18, c, H);
  }
  return out;
}

/** The first step from the base downwards whose contrast with `fg` passes. */
function darkenUntil(bg: string, fg: string, target: number, steps: Scale): string {
  if (contrast(bg, fg) >= target) {
    return bg;
  }
  for (const step of ['600', '700', '800', '900']) {
    if (contrast(at(steps, step), fg) >= target) {
      return at(steps, step);
    }
  }
  return at(steps, '900');
}

/** Text on a filled colour: white if it passes AA, else the dark text colour. */
function onColour(bg: string, light = WHITE, dark?: string): string {
  if (contrast(bg, light) >= 4.5) {
    return light;
  }
  if (dark && contrast(bg, dark) >= 4.5) {
    return dark;
  }
  return contrast(bg, light) > contrast(bg, dark ?? '#000000') ? light : (dark as string);
}

export function build(brand: BrandInput): BrandTokens {
  const primary = scale(brand.primary);
  const accent = brand.accent ? scale(brand.accent) : undefined;
  const neutral = neutralScale(brand.neutralTintFrom ?? brand.primary, brand.neutralTint ?? 'warm');
  const status = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, scale(v)])) as Record<string, Scale>;
  const text = brand.text ?? at(neutral, '900');

  // The brand colour stays for brand surfaces (header band, large type, illustrations); actions use the darkest-needed step
  // so white labels pass 4.5:1.
  const action = darkenUntil(at(primary, 'base'), WHITE, 4.5, primary);
  const actionHover = action !== at(primary, '700') ? at(primary, '700') : at(primary, '800');
  const light: Roles = {
    bg: at(neutral, '0'), surface: at(neutral, '0'), 'surface-raised': at(neutral, '50'), 'surface-sunken': at(neutral, '100'),
    text, 'text-muted': at(neutral, '600'), 'text-subtle': at(neutral, '500'),
    border: at(neutral, '200'), 'border-strong': at(neutral, '300'),
    brand: at(primary, 'base'), 'on-brand': onColour(at(primary, 'base'), WHITE, text),
    primary: action, 'primary-hover': actionHover, 'primary-active': at(primary, '800'),
    'on-primary': WHITE,
    'primary-subtle': at(primary, '50'), 'on-primary-subtle': at(primary, '800'),
    link: darkenUntil(at(primary, 'base'), at(neutral, '0'), 4.5, primary),
    price: text, 'price-old': at(neutral, '500'),
    'sold-out': at(neutral, '400'),
    'focus-ring': darkenUntil(at(primary, '500'), at(neutral, '0'), 3.0, primary),
    overlay: 'rgba(17, 17, 17, 0.5)',
  };
  if (accent) {
    light.accent = at(accent, 'base');
    light['on-accent'] = onColour(at(accent, 'base'), WHITE, text);
  }
  for (const [name, sc] of Object.entries(status)) {
    light[name] = darkenUntil(at(sc, '600'), WHITE, 4.5, sc);
    light[`on-${name}`] = WHITE;
    light[`${name}-subtle`] = at(sc, '50');
    light[`on-${name}-subtle`] = at(sc, '800');
  }
  // Warning is used as a badge on amber, with dark text, which is how people read it.
  const warning = status.warning as Scale;
  light.warning = at(warning, '400');
  light['on-warning'] = onColour(at(warning, '400'), WHITE, at(neutral, '950'));

  const darkBg = at(neutral, '950');
  const dark: Roles = {
    bg: darkBg, surface: at(neutral, '900'), 'surface-raised': at(neutral, '800'), 'surface-sunken': darkBg,
    text: at(neutral, '50'), 'text-muted': at(neutral, '300'), 'text-subtle': at(neutral, '400'),
    border: at(neutral, '800'), 'border-strong': at(neutral, '700'),
    brand: at(primary, 'base'), 'on-brand': light['on-brand'] as string,
    primary: at(primary, '400'), 'primary-hover': at(primary, '300'), 'primary-active': at(primary, '200'),
    'on-primary': onColour(at(primary, '400'), WHITE, at(neutral, '950')),
    'primary-subtle': at(primary, '900'), 'on-primary-subtle': at(primary, '100'),
    link: at(primary, '300'), price: at(neutral, '50'), 'price-old': at(neutral, '400'), 'sold-out': at(neutral, '600'),
    'focus-ring': at(primary, '300'), overlay: 'rgba(0, 0, 0, 0.6)',
  };
  if (accent) {
    dark.accent = at(accent, 'base');
    dark['on-accent'] = light['on-accent'] as string;
  }
  for (const [name, sc] of Object.entries(status)) {
    dark[name] = at(sc, '400');
    dark[`on-${name}`] = onColour(at(sc, '400'), WHITE, at(neutral, '950'));
    dark[`${name}-subtle`] = at(sc, '900');
    dark[`on-${name}-subtle`] = at(sc, '100');
  }

  // Brand-specific roles override or extend the defaults, and go through the same contrast checks.
  Object.assign(light, brand.roles ?? {});
  if (!brand.roles || !('link' in brand.roles)) {
    // Links are checked against the page as the brand sets it (e.g. a grey page), not against white.
    light.link = darkenUntil(at(primary, 'base'), light.bg as string, 4.5, primary);
  }
  Object.assign(dark, brand.darkRoles ?? {});

  const scales: Record<string, Scale> = { brand: primary, neutral, ...status };
  if (accent) {
    scales.accent = accent;
  }
  return { scales, light, dark };
}

/**
 * The website's own surfaces for a brand that doesn't set them (`roles`): the dark header, the brand band (footer, banners),
 * a soft brand tint, and a second voice. Derived from the scales so they pass the same checks; a brand's own roles win.
 */
export function withSiteRoles({ scales, light, dark }: BrandTokens): BrandTokens {
  const brand = scales.brand as Scale;
  const neutral = scales.neutral as Scale;
  const site: Roles = {
    header: at(neutral, '950'), 'on-header': WHITE,
    band: light.primary as string, 'on-band': light['on-primary'] as string,
    tint: at(brand, '100'),
    secondary: at(neutral, '900'), 'on-secondary': WHITE, 'secondary-subtle': at(neutral, '100'),
  };
  const siteDark: Roles = {
    header: at(neutral, '900'), 'on-header': at(neutral, '50'),
    band: light.primary as string, 'on-band': light['on-primary'] as string,
    tint: at(brand, '900'),
    secondary: at(neutral, '200'), 'on-secondary': at(neutral, '950'), 'secondary-subtle': at(neutral, '800'),
  };
  return { scales, light: { ...site, ...light }, dark: { ...siteDark, ...dark } };
}

/** A pair every theme must pass. */
export interface ContrastCheck {
  fg: string;
  bg: string;
  foreground: string;
  background: string;
  ratio: number;
  minimum: number;
  use: string;
  ok: boolean;
}

const PAIRS: [string, string, number, string][] = [
  ['text', 'bg', 4.5, 'body text'], ['text-muted', 'bg', 4.5, 'secondary text'],
  ['text-subtle', 'bg', 3.0, 'hints, large text'],
  ['on-primary', 'primary', 4.5, 'button label'], ['on-primary', 'primary-hover', 4.5, 'button label, hover'],
  ['on-primary-subtle', 'primary-subtle', 4.5, 'selected chip'], ['link', 'bg', 4.5, 'links'],
  ['price', 'surface', 4.5, 'prices'], ['focus-ring', 'bg', 3.0, 'focus ring'],
  ['border-strong', 'bg', 1.5, 'input border (with label)'],
  ['on-success', 'success', 4.5, 'success badge'], ['on-danger', 'danger', 4.5, 'error badge'],
  ['on-warning', 'warning', 4.5, 'warning badge'], ['on-info', 'info', 4.5, 'info badge'],
  ['on-danger-subtle', 'danger-subtle', 4.5, 'error message'],
];

export function check(roles: Roles): ContrastCheck[] {
  const row = (fg: string, bg: string, minimum: number, use: string): ContrastCheck => {
    const foreground = roles[fg] as string;
    const background = roles[bg] as string;
    const ratio = contrast(foreground, background);
    return { fg, bg, foreground, background, ratio, minimum, use, ok: ratio >= minimum };
  };
  const rows = PAIRS.map(([fg, bg, minimum, use]) => row(fg, bg, minimum, use));
  if ('accent' in roles) {
    rows.push(row('on-accent', 'accent', 4.5, 'deal badge'));
  }
  for (const [fg, bg, use] of [
    ['on-secondary', 'secondary', 'second brand voice'],
    ['on-header', 'header', 'header text'],
    ['on-band', 'band', 'footer and band text'],
  ] as const) {
    if (fg in roles && bg in roles) {
      rows.push(row(fg, bg, 4.5, use));
    }
  }
  rows.push(row('on-brand', 'brand', 3.0, 'large text on brand band'));
  return rows;
}

/** Contrast failures in every mode the brand uses; empty when the theme can be published. */
export function failures(brand: BrandInput, tokens: BrandTokens): ContrastCheck[] {
  const modes = brand.darkMode === false ? [tokens.light] : [tokens.light, tokens.dark];
  return modes.flatMap((roles) => check(roles).filter((r) => !r.ok));
}

export interface Foundation {
  font: Record<string, string>;
  fontSize: Record<string, number>;
  lineHeight: Record<string, number>;
  fontWeight: Record<string, number>;
  radius: Record<string, number>;
  space: Record<string, number>;
  size: Record<string, number>;
  shadow: Record<string, string>;
  duration: Record<string, number>;
  easing: Record<string, string>;
  breakpoint: Record<string, number>;
}

export function foundation(brand: BrandInput): Foundation {
  const radius = { sharp: 4, soft: 8, round: 16 }[brand.radius ?? 'soft'];
  return {
    font: brand.fonts,
    fontSize: { xs: 12, sm: 14, md: 16, lg: 18, xl: 20, '2xl': 24, '3xl': 28, '4xl': 32, '5xl': 40 },
    lineHeight: { tight: 1.25, snug: 1.375, body: 1.5 },
    fontWeight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    radius: { control: radius, card: Math.round(radius * 1.5), sheet: radius * 2, pill: 9999 },
    space: Object.fromEntries([0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 30].map((k) => [String(k), k * 4])),
    size: { touch: 44, 'button-sm': 36, 'button-md': 44, 'button-lg': 52, input: 48, 'cart-bar': 64 },
    shadow: {
      card: '0 1px 2px rgb(0 0 0 / 0.06), 0 1px 3px rgb(0 0 0 / 0.10)',
      raised: '0 4px 6px -1px rgb(0 0 0 / 0.10), 0 2px 4px -2px rgb(0 0 0 / 0.08)',
      overlay: '0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.10)',
    },
    duration: { fast: 150, base: 250, slow: 350 },
    easing: { standard: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.3, 0, 1, 1)' },
    breakpoint: { sm: 640, md: 768, lg: 1024, xl: 1280 },
  };
}
